// src/pages/MagazineCategories.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { motion } from "framer-motion";

export default function MagazineCategories() {
  const navigate = useNavigate();
  const magazinesPerPage = 30;
  const [searchParams] = useSearchParams();
  const mp_id = searchParams.get("mp_id");
  const sp_id = searchParams.get("sp_id");
  const [magazines, setMagazines] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Fetch magazines from API
  useEffect(() => {
    const fetchMagazines = async () => {
      setLoading(true);
      setMagazines([]); // 🔑 clear old data
      setCurrentPage(1);

      try {
        // CASE 1: Category filter present
        if (mp_id && sp_id) {
          const payload = {
            mpid: mp_id,
            spid: sp_id,
          };

          console.log("Category payload →", payload);
          const res = await fetch(`${API_BASE_URL}magazine_category_list.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              mp_id: mp_id,
              sp_id: sp_id,
            }),
          });

          const data = await res.json();
          console.log("Fetched magazines by category:", data);
          if (data?.head?.code === 200) {
            setMagazines(
              data.body.map((mag) => ({
                title: `Magazine ${mag.mag_id}`,
                img: mag.cover_image,
                publish: formatPublishDate(mag.publish_date),
                mag_id: mag.mag_id,
                mp_id: mag.mp_id,
                sp_id: mag.sp_id,
              }))
            );
          } else {
            setMagazines([]);
          }
        }

        // CASE 2: No category → load all magazines
        else {
          const res = await fetch(`${API_BASE_URL}magazine_list.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({}),
          });

          const data = await res.json();
          console.log("Fetched all magazines:", data);
          if (data?.head?.code === 200) {
            setMagazines(
              data.body.map((mag) => ({
                title: `Magazine ${mag.mag_id}`,
                img: mag.cover_image,
                publish: formatPublishDate(mag.publish_date),
                mag_id: mag.mag_id,
                mp_id: mag.mp_id,
                sp_id: mag.sp_id,
              }))
            );
          } else {
            setMagazines([]);
          }
        }
      } catch (err) {
        console.error("Error fetching magazines:", err);
        setMagazines([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMagazines();
  }, [API_BASE_URL, mp_id, sp_id]);

  // Navigate to subscription page with magazine ID
  const handleMagazineClick = (mag) => {
    navigate(`/subscription/${mag.mag_id}`, { state: { mag } });
  };

  // Format publish_date from API to 'Mon YYYY'
  const formatPublishDate = (dateStr) => {
    const date = new Date(dateStr);
    const month = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear();
    return `${month} ${year}`;
  };

  //page loader
  const PageLoader = () => (
  <div
    style={{
      minHeight: "70vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
    }}
  >
    <div
      className="spinner-border"
      role="status"
      style={{ width: "3rem", height: "3rem", color: "#f64a1aff" }}
    />
    <p className="mt-3 fw-semibold">Loading magazines...</p>
  </div>
);


  // Pagination calculations
  const totalPages = Math.ceil(magazines.length / magazinesPerPage);
  const indexOfLast = currentPage * magazinesPerPage;
  const indexOfFirst = indexOfLast - magazinesPerPage;
  const currentMagazines = magazines.slice(indexOfFirst, indexOfLast);

  return (
    <div
      className="container-fluid"
      style={{
        minHeight: "100vh",
        marginTop: "60px",
        paddingLeft: 0,
        paddingRight: 0,
      }}
    >
      {/* HERO SECTION */}
      <motion.section
        className="text-center d-flex align-items-center justify-content-center mb-4"
        style={{
          minHeight: "20vh",
          background: "#0b2747",
          color: "white",
          padding: "30px 20px",
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="container">
          <motion.h1
            className="display-4 fw-bold"
            initial={{ y: -50 }}
            animate={{ y: 0 }}
            transition={{ duration: 1 }}
          >
            Porvaall Magazines
          </motion.h1>
          <motion.p
            className="lead mt-3"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 1 }}
          >
            Inspiring knowledge. Empowering minds. Connecting ideas.
          </motion.p>
        </div>
      </motion.section>

      {/* MAGAZINES GRID */}
      <div className="container">
        {loading ? (
          <PageLoader />
        ) : currentMagazines.length === 0 ? (
          <p className="text-center">No magazines found.</p>
        ) : (
          <div className="row g-4">
            {currentMagazines.map((mag, i) => (
              <motion.div
                key={i}
                className="col-6 col-sm-6 col-md-4 col-lg-3 col-xl-2"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="text-center"
                >
                  <div
                    className="card shadow-sm border-0"
                    style={{ borderRadius: "6px" }}
                  >
                    {/* inside currentMagazines.map((mag, i) => ... */}
                    <img
                      loading="lazy"
                      src={mag.img}
                      alt={mag.title}
                      onClick={() => {
                        window.scrollTo(0, 0);
                        // 1. Navigate to the dynamic path using the ID
                        // 2. We still pass 'state' as a fallback for instant loading
                        navigate(
                          `/subscription/${mag.mag_id}?mp_id=${mag.mp_id}&sp_id=${mag.sp_id}`,
                          {
                            state: {
                              mag_id: mag.mag_id,
                              mp_id: mag.mp_id,
                              sp_id: mag.sp_id,
                              cover_image: mag.img,
                              publish: mag.publish,
                            },
                          }
                        );
                      }}
                      style={{
                        width: "100%",
                        aspectRatio: "2.8 / 4",
                        objectFit: "cover",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    />
                  </div>
                  <p
                    className="mt-2 mb-1 fw-semibold"
                    style={{ fontSize: "1rem", textAlign: "center" }}
                  >
                    {mag.publish}
                  </p>
                </motion.div>
              </motion.div>
            ))}
          </div>
        )}

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-4">
            <nav>
              <ul className="pagination">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => {
                      if (currentPage > 1) setCurrentPage(currentPage - 1);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    Prev
                  </button>
                </li>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (num) => (
                    <li
                      key={num}
                      className={`page-item ${
                        currentPage === num ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => {
                          setCurrentPage(num);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        {num}
                      </button>
                    </li>
                  )
                )}

                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => {
                      if (currentPage < totalPages)
                        setCurrentPage(currentPage + 1);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
}
